'use client'

import { ChevronLeftIcon } from '@heroicons/react/24/outline'
import closeIcon from '@public/images/close_white.svg'
import Image from 'next/image'

import { Button } from '@src/components/Button/Button'
import { LocalizedLink } from '@src/components/LocalizedLink/LocalizedLink'
import { useTopNavV6 } from '@src/components/TopNavV6/TopNavV6Context'
import type { TopNavV6SubNavItem } from '@src/components/TopNavV6/TopNavV6Props'
import { useTrackSelectNavCta } from '@src/utils/analytics.util'

interface TopNavV6MobileMiniMenuProps {
  subNavItem: TopNavV6SubNavItem
}

export const TopNavV6MobileMiniMenu = ({ subNavItem }: TopNavV6MobileMiniMenuProps) => {
  const trackSelectNavCta = useTrackSelectNavCta()
  const { setSelectedThirdLevelMenu, setIsMobileMenuOpen } = useTopNavV6()

  const handleBack = () => {
    setSelectedThirdLevelMenu(null)
  }

  const handleClose = () => {
    setSelectedThirdLevelMenu(null)
    setIsMobileMenuOpen(false)
  }

  const handleItemClick = (href?: string) => {
    if (href) {
      trackSelectNavCta({ 'cta url': href })
      setIsMobileMenuOpen(false)
      setSelectedThirdLevelMenu(null)
    }
  }

  const miniMenu = subNavItem.miniMenu

  if (!miniMenu) return null

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="flex h-12 items-center justify-between border-b border-bodi-gray-dark bg-gradient-to-r from-white/[0.08] to-white/[0.08] px-4">
        <button
          className="flex size-6 appearance-none items-center justify-center"
          onClick={handleBack}
          data-testid="backMiniMenu"
        >
          <ChevronLeftIcon className="size-6 text-bodi-green-bright-500" />
        </button>
        <div className="flex flex-1 items-center justify-center">
          <p className="text-body-md leading-[18px] font-semibold text-bodi-gray-butters uppercase">
            {subNavItem.title}
          </p>
        </div>
        <button
          className="flex size-6 appearance-none items-center justify-center"
          onClick={handleClose}
          data-testid="closeMiniMenu"
        >
          <Image src={closeIcon} width={20} height={20} alt="Close menu" />
        </button>
      </div>

      {/* Content */}
      <div className="flex flex-1 flex-col gap-3 overflow-y-auto px-4 pt-8 pb-8">
        {miniMenu.items.map((item, itemIndex) => {
          return (
            <button
              key={`item-${itemIndex}`}
              className="flex w-full items-center text-left"
              onClick={() => {
                handleItemClick(item.href)
                item.onClick?.()
              }}
            >
              <div className="flex items-center gap-1">
                <p className="text-body-sm leading-5 text-bodi-gray-butters">{item.title}</p>
                {item.badge && (
                  <div className="flex items-center gap-1 text-body-sm leading-5">
                    <span className="font-sans font-normal text-bodi-gray-butters">-</span>
                    <span className="text-center font-sans font-medium text-bodi-green-bright-500">
                      {item.badge}
                    </span>
                  </div>
                )}
              </div>
            </button>
          )
        })}
      </div>

      {/* Bottom CTA Button */}
      {subNavItem.ctaButton && (
        <div className="border-t border-bodi-gray-dark bg-bodi-black-text px-4 py-5">
          {subNavItem.ctaButton.href ? (
            <LocalizedLink
              href={subNavItem.ctaButton.href}
              onClick={() => {
                handleItemClick(subNavItem.ctaButton!.href)
                subNavItem.ctaButton?.onClick?.()
              }}
            >
              <Button className="w-full rounded-full" size="medium" variant="primary">
                {subNavItem.ctaButton.title}
              </Button>
            </LocalizedLink>
          ) : (
            <button
              onClick={() => {
                subNavItem.ctaButton?.onClick?.()
              }}
              className="w-full"
            >
              <Button className="w-full rounded-full" size="medium" variant="primary">
                {subNavItem.ctaButton.title}
              </Button>
            </button>
          )}
        </div>
      )}
    </div>
  )
}
